import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@features/auth/store/authStore';
import { useProactiveRefresh } from '@features/auth/hooks/useProactiveRefresh';
import { SupportReportButton } from '@features/support/components/SupportReportButton';
import { SupportWelcomeWizard } from '@features/support/components/SupportWelcomeWizard';
import { SupportErrorProvider } from '@features/support/SupportErrorProvider';
import { AppRoutes } from './routes';

function AppInner() {
  const queryClient = useQueryClient();
  const { isAuthenticated, clearAuth } = useAuthStore();

  // Proactively refresh the JWT access token before it expires so
  // users never hit a 401 during normal usage. Also handles tab
  // wake-up after laptop sleep.
  useProactiveRefresh();

  // Sync logout across browser tabs via StorageEvent
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If accessToken was removed in another tab, sync logout here
      if (e.key === 'accessToken' && e.newValue === null && isAuthenticated) {
        clearAuth();
        queryClient.clear();
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, clearAuth, queryClient]);

  return (
    <SupportErrorProvider>
      <AppRoutes />
      {isAuthenticated && <SupportReportButton />}
      {isAuthenticated && <SupportWelcomeWizard />}
    </SupportErrorProvider>
  );
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
